import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../../design-system/primitives/Icon';

/**
 * Shows `value` only while the eye button is held (pointer or Space/Enter).
 * The winning engine method is deliberately not left on screen: it is the one
 * field staff must not read out to a customer, so it never survives a glance.
 */
const HoldToReveal = ({ value, hint }) => {
  const [held, setHeld] = useState(false);
  const release = useCallback(() => setHeld(false), []);

  // A pointer released outside the button (fast drag) still hides.
  useEffect(() => {
    if (!held) return undefined;
    window.addEventListener('pointerup', release);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('blur', release);
    };
  }, [held, release]);

  if (!value) return <span className="qa-muted">—</span>;

  return (
    <span className="qa-reveal">
      <button
        type="button"
        className={`qa-reveal__btn ${held ? 'qa-reveal__btn--held' : ''}`}
        aria-label="Hold to reveal model"
        aria-pressed={held}
        onPointerDown={(e) => { e.preventDefault(); setHeld(true); }}
        onPointerUp={release}
        onPointerLeave={release}
        onPointerCancel={release}
        onBlur={release}
        onKeyDown={(e) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); setHeld(true); }
        }}
        onKeyUp={(e) => { if (e.key === ' ' || e.key === 'Enter') release(); }}
      >
        <Icon name={held ? 'eye' : 'eye-off'} size={13} />
      </button>
      <span className={`qa-reveal__value ${held ? '' : 'qa-reveal__value--hidden'}`}>
        {held ? value : '••••••'}
      </span>
      {held && hint && <span className="qa-reveal__hint">{hint}</span>}
    </span>
  );
};

export default HoldToReveal;
